import { useCallback, useRef } from 'react';

/**
 * Returns a ref callback. The first time the attached element becomes at least `threshold`
 * visible, `onInView` fires once. Used for impression events, where the denominator has to
 * count "the user actually saw this" rather than "the page loaded".
 *
 * This is a ref *callback* rather than a ref object on purpose: the element it watches is
 * usually behind a loading state, so it mounts after the component's first render. An effect
 * keyed on a ref object would look once, find nothing, and never re-run.
 *
 * If IntersectionObserver is unavailable we fire immediately rather than never. Both are
 * wrong, but over-counting keeps an impression denominator >= its click numerator, whereas
 * under-counting can produce a clickthrough rate above 100% for that slice -- which is the
 * failure mode that actually misleads someone reading the number.
 */
export function useInViewOnce<T extends HTMLElement>(onInView: () => void, threshold = 0.5) {
  const hasFired = useRef(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const onInViewRef = useRef(onInView);
  onInViewRef.current = onInView;

  return useCallback(
    (element: T | null) => {
      observer.current?.disconnect();
      observer.current = null;

      if (!element || hasFired.current) {
        return;
      }

      const fireOnce = () => {
        if (hasFired.current) {
          return;
        }
        hasFired.current = true;
        onInViewRef.current();
      };

      if (typeof IntersectionObserver === 'undefined') {
        fireOnce();
        return;
      }

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            fireOnce();
            observer.current?.disconnect();
            observer.current = null;
          }
        },
        { threshold },
      );

      observer.current.observe(element);
    },
    [threshold],
  );
}
